import React, { useMemo } from "react";

import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import fr from "i18n-iso-countries/langs/fr.json";
import { Card, Col, Form, Row } from "react-bootstrap";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { Controller } from "react-hook-form";

import { getCurrentLang } from "@/utils/Local";

import i18n from "../../../i18n";

countries.registerLocale(en);
countries.registerLocale(fr);

interface ContactFormLocationSectionProps {
  methods: any;
}

const ContactFormLocationSection: React.FC<ContactFormLocationSectionProps> = ({
  methods,
}): React.JSX.Element => {
  const { t } = i18n;
  const priorityOptions = useMemo(() => ["CA", "US"], []);
  const country = methods.watch("location.country");
  const lang = getCurrentLang();

  const getRegionLabel = useMemo(() => {
    if (country === "CA") {
      return t("Province");
    }
    if (country === "US") {
      return t("State");
    }
    return t("Region");
  }, [country]);

  return (
    <Row>
      <Col xs={12}>
        <Card.Title>{t("Location")}</Card.Title>
      </Col>
      <Col lg={4} xs={12}>
        <Form.Group className="mb-3" controlId="location-country">
          <Form.Label>{t("Country")}</Form.Label>
          <Controller
            control={methods.control}
            name="location.country"
            render={({ field: { onChange, value } }) => (
              <CountryDropdown
                className={`form-select ${methods.formState.errors.location?.country ? "is-invalid" : ""}`}
                defaultOptionLabel={`-- ${t("Please select an option")} --`}
                priorityOptions={priorityOptions}
                value={value}
                valueType="short"
                customRender={({ options, ...rest }) => {
                  // options are [{ value, label }, ...] but label is English; replace it:
                  const localized = options.map((o) => ({
                    ...o,
                    label: countries.getName(o.value, lang) ?? o.label,
                  }));
                  return (
                    <select {...rest}>
                      {localized.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  );
                }}
                onChange={(e) => {
                  onChange(e);
                  void methods.trigger(["location.country", "location.region"]);
                }}
              />
            )}
          />
          <Form.Control.Feedback className="d-block" type="invalid">
            {t(methods.formState.errors.location?.country?.message)}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col lg={4} xs={12}>
        <Form.Group className="mb-3" controlId="location-region">
          <Form.Label>{getRegionLabel}</Form.Label>
          <Controller
            control={methods.control}
            name="location.region"
            render={({ field: { onChange, value } }) => (
              <RegionDropdown
                className={`form-select ${methods.formState.errors.location?.region ? "is-invalid" : ""}`}
                country={country}
                countryValueType="short"
                defaultOptionLabel={`-- ${t("Please select an option")} --`}
                disableWhenEmpty={!country}
                value={value}
                valueType="short"
                onChange={(e) => {
                  onChange(e);
                  void methods.trigger(["location.region"]);
                }}
              />
            )}
          />
          <Form.Control.Feedback className="d-block" type="invalid">
            {t(methods.formState.errors.location?.region?.message)}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col lg={4} xs={12}>
        <Form.Group className="mb-3" controlId="location-city">
          <Form.Label>{t("City")}</Form.Label>
          <Form.Control
            type="text"
            {...methods.register("location.city")}
            isInvalid={!!methods.formState.errors.location?.city}
          />
          <Form.Control.Feedback type="invalid">
            {t(methods.formState.errors.location?.city?.message)}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
    </Row>
  );
};

export default ContactFormLocationSection;
